import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { AppButton, AppIconButton } from '../../design-system';
import {
  COGNEX_LIGHT_MODE_OPERATOR_MAX,
  OPERATOR_COGNEX_LIGHT_MODES,
  type LightModeEntry,
} from '@cnapse/shared/schemas.js';

const MIN_MODES = 1;

export interface LightingModesEditorProps {
  value: LightModeEntry[];
  onChange: (modes: LightModeEntry[]) => void;
  disabled?: boolean;
}

export function LightingModesEditor({ value, onChange, disabled = false }: LightingModesEditorProps) {
  const updateRow = (index: number, patch: Partial<LightModeEntry>) => {
    const next = value.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  const addRow = () => {
    const usedModes = new Set(value.map((m) => m.mode));
    const nextMode =
      OPERATOR_COGNEX_LIGHT_MODES.find((m) => !usedModes.has(m)) ?? OPERATOR_COGNEX_LIGHT_MODES[0];
    onChange([...value, { mode: nextMode, startPower: 2 }]);
  };

  const removeRow = (index: number) => {
    if (value.length <= MIN_MODES) return;
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Starting lighting modes
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Tried in order when lighting source is &quot;Template starting modes&quot;. Modes 0–
        {COGNEX_LIGHT_MODE_OPERATOR_MAX} only.
      </Typography>
      <Stack spacing={1.5}>
        {value.map((row, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
            <FormControl size="small" sx={{ minWidth: 100 }} disabled={disabled}>
              <InputLabel>Mode</InputLabel>
              <Select
                label="Mode"
                value={row.mode}
                onChange={(e) => updateRow(index, { mode: Number(e.target.value) })}
              >
                {OPERATOR_COGNEX_LIGHT_MODES.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {mode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flex: 1, px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Start power: {row.startPower}
              </Typography>
              <Slider
                size="small"
                min={0}
                max={127}
                value={row.startPower}
                disabled={disabled}
                onChange={(_, v) => updateRow(index, { startPower: v as number })}
              />
            </Box>
            <AppIconButton
              size="small"
              aria-label="Remove lighting mode"
              disabled={disabled || value.length <= MIN_MODES}
              onClick={() => removeRow(index)}
            >
              <DeleteIcon fontSize="small" />
            </AppIconButton>
          </Stack>
        ))}
      </Stack>
      <AppButton
        size="small"
        variant="text"
        startIcon={<AddIcon />}
        sx={{ mt: 1 }}
        disabled={disabled}
        onClick={addRow}
      >
        Add mode
      </AppButton>
    </Box>
  );
}
